/**
 * Instruction Plan — Enterprise Semantic Template & Scaffolding Platform.
 *
 * Consolidates the two operational todo lists produced during the
 * Enterprise upgrade evaluation for `evaldez@prompt.tax`:
 *
 *   1. "Remaining work — checklist for the technical team"
 *      (phase-tagged gaps identified against
 *      `analysis3/INVESTIGATION_TRACKING.md`).
 *   2. "What's missing to close the gate before shipping
 *      'Enterprise-grade guardrails' as a public claim"
 *      (agent-runtime guardrail-parity items — see
 *      `apps/agents/runtime.py` vs `apps/agents/runtime_langgraph.py`).
 *
 * Reachable at `/plan`. Rendered inside the standard `Layout` so it
 * inherits the tenant nav and auth guard used by other operational
 * pages (Studio, Composer, Agents). No emojis anywhere — keeps the
 * enterprise register.
 */
import { Link } from "@tanstack/react-router";
import {
  AlertTriangle,
  BookOpen,
  CheckCircle2,
  Circle,
  ClipboardList,
  ExternalLink,
  ShieldCheck,
} from "lucide-react";

type Status = "done" | "in-progress" | "not-started" | "blocked";

type TrackItem = {
  id: string;
  ref: string;
  title: string;
  detail: string;
  owner?: string;
  status: Status;
  link?: { to: string; label: string };
};

const TECHNICAL_TRACK: TrackItem[] = [
  {
    id: "10.6",
    ref: "Phase 10.6",
    title: "Healthcare Tier 2 industry gate (skeleton)",
    detail:
      "`ont-semantic-model/tier-2/healthcare-core.draft.ttl` shipped as a category-name skeleton (Encounter, Diagnosis, Practitioner, Patient, Observation, Procedure, MedicationRequest, CareTeam, Condition — no coded-terminology bindings). Every class carries `skos:note \"DRAFT — pending SME review\"`. `apps.composition.validators.draft_gate.enforce_draft_gate` refuses to materialise a scaffold containing DRAFT classes. Blocked on: clinical informatician review, HIPAA/GDPR PIA, SNOMED CT / ICD-10 / LOINC / FHIR licensing decisions.",
    status: "in-progress",
    owner: "Clinical SME + Legal",
  },
  {
    id: "3.7",
    ref: "Phase 3.7",
    title: "Tier 4 (LOB) industry-line catalogue (skeleton)",
    detail:
      "`ont-semantic-model/tier-4/lob-insurance.draft.ttl` shipped as a Home/Health line-of-business skeleton (HomeInsuranceProduct, HomeDwelling, HomePeril, HealthInsuranceProduct, CoverageBenefit, ProviderNetwork). All DRAFT-gated. Blocked on: actuarial SME for peril taxonomy + product-line SME per LOB per market.",
    status: "in-progress",
    owner: "Ontology WG + Actuarial SME",
  },
  {
    id: "3.8",
    ref: "Phase 3.8",
    title: "Tier 5 (Regional) DE / UK / XL overlays (skeleton)",
    detail:
      "`ont-semantic-model/tier-5/regional-overlays.draft.ttl` shipped with jurisdiction skeletons (DEJurisdiction, UKJurisdiction, BermudaJurisdiction) and regulator category placeholders (DERegulator, UKRegulator subClassOf regulatory-core Authority). No proprietary regulator taxonomies — SMEs to instantiate BaFin / GDV / PRA / FCA / FOS / BMA. All DRAFT-gated. Multi-standard bridges (FIBO / ACORD / FHIR skos:exactMatch) tracked separately once external-standard licensing is closed.",
    status: "in-progress",
    owner: "Regulatory Counsel per jurisdiction",
  },
  {
    id: "3.11",
    ref: "Phase 3.11",
    title: "Non-insurance industry semantic review",
    detail:
      "Author domain SME reviews for `regulatory-banking`, then extend to healthcare/logistics/regional once the DRAFT skeletons (10.6 / 3.7 / 3.8) receive SME sign-off. Adds citations to the pack manifests. The DRAFT-gate validator is the mechanical enforcement of this review step: no DRAFT-annotated class survives scaffold generation.",
    status: "not-started",
    owner: "Domain SMEs",
  },
  {
    id: "7.2",
    ref: "Phase 7.2",
    title: "Bnode-safe ontology diff (design-tier re-fix)",
    detail:
      "The v1 `apps/packs/diff.py` string-orders triples then string-diffs, so two OWL-isomorphic graphs (semantically identical, different rdflib bnode allocation) show phantom adds+removes. Replaced by `apps/ontology/rdf_diff.py` using `rdflib.compare.to_isomorphic` + `graph_diff`. Old module retained for backward compat with a regression-lock test that fails if anyone silently 'fixes' it.",
    status: "done",
  },
  {
    id: "4.3",
    ref: "Phase 4.3",
    title: "Tier 4 (LOB) and Tier 5 (Regional) pack publication",
    detail:
      "Publisher already generic — `sync_ontology_packs` accepts any `manifest.kind` / `manifest.tier` combination. Code-complete; blocked only on 3.7 / 3.10 content authoring by the Ontology WG.",
    status: "done",
    link: { to: "/packs", label: "Marketplace" },
  },
  {
    id: "6.5",
    ref: "Phase 6.5",
    title: "Side-by-side ontology diff (bnode-safe)",
    detail:
      "`POST /studio/sessions/{id}/preview-diff/` returns a triple-level, blank-node-isomorphism-aware diff between the workspace's currently PUBLISHED `OntologyVersion` and the composed candidate. Diff engine `apps.ontology.rdf_diff.diff_ttl` uses `rdflib.compare.to_isomorphic` + `graph_diff` — a bnode-only relabel yields zero structural diff, a rdfs:label edit is reported as a label-change (not an add+remove pair). Rendered as `DiffPanel` in the Composer preview step; 10/10 diff-engine tests and one backward-compat lock against the old `apps/packs/diff.py`.",
    status: "done",
    link: { to: "/studio/composer", label: "Composer" },
  },
  {
    id: "6.7",
    ref: "Phase 6.7",
    title: "Monaco viewer for composed TTL",
    detail:
      "Ship a read-only Monaco panel behind an Enterprise entitlement so architects can inspect the composed TTL/SHACL before saving a CompositionSpec. Design complete; deferred to follow-up (300+ LoC + `@monaco-editor/react` dep).",
    status: "not-started",
  },
  {
    id: "9.1",
    ref: "Phase 9.1",
    title: "Regulated multilingual labels",
    detail:
      "`apps.composition.label_audit.enforce_multilingual_labels` refuses scaffold generation when a declared language has < 100% `rdfs:label` coverage. Wired into `POST /studio/sessions/{id}/preview-scaffold/`; HTTP 422 with per-language shortfall.",
    status: "done",
  },
  {
    id: "10.4",
    ref: "Phase 10.4",
    title: "Documentation refresh",
    detail:
      "Sync `docs/architecture-studio-v2-plan.md`, `docs/ontology-packs.md` and `docs/from-ontology-to-production.md` with the shipped Composer wizard and pack catalogue.",
    status: "in-progress",
    link: { to: "/docs", label: "Docs" },
  },
  {
    id: "10.5",
    ref: "Phase 10.5",
    title: "Marketing polish for the composed narrative",
    detail:
      "Public pages: Enterprise Composer decision-table (Why Ontoligent), ROI story, developer experience terminal. Ongoing; the three sections shipped in this iteration.",
    status: "done",
    link: { to: "/", label: "Public homepage" },
  },
  {
    id: "3.9b",
    ref: "Phase 3.9b",
    title: "Golden-query regression suite — Library 1 (24 queries)",
    detail:
      "`apps/composition/tests/test_golden_queries_library1.py` extends the Phase 3.9 starter suite by freezing all 24 named Tier-2 SPARQL queries in `ont-semantic-model/queries/tier-2/*.rq` (5 party + 5 policy + 6 claims + 4 financial + 4 cross-domain) against the merged 8-pack insurance ontology. Two locks per query: (1) `prepareQuery` syntax lock, (2) every `ont-*:*` token declared in the ontology (broadened to accept OWL individuals typed via domain classes, not just W3C metavocab types). 49/49 pass. Caught 1 real transcription bug — Q-FIN-004 used `ont-pol:isCededUnder` but the property lives in `ont-fin:` — now permanently locked against re-regression.",
    status: "done",
    link: { to: "/studio/composer", label: "Composer" },
  },
  {
    id: "3.3c",
    ref: "Phase 3.3",
    title: "Regulatory instrument completeness lock",
    detail:
      "`apps/composition/tests/test_regulatory_completeness.py` freezes the tracker line 709 promise: `regulatory-insurance.ttl` MUST declare IDD, Solvency II, IFRS 17, DORA, SFCR, ORSA, IPID. Parametrised term-declaration test (7 params) + `@en rdfs:label` coverage check. 8/8 pass. Silent removal or renaming of any of the seven instruments now breaks CI — no more contract drift between the tracker's declared scope and shipped ontology.",
    status: "done",
  },
  {
    id: "A.3",
    ref: "Track A.3",
    title: "Marketplace Tier 1 — distinct FIBO / FHIR / ACORD tiles",
    detail:
      "`apps/studio/services.py` `COMPOSER_STEPS[standards]` now carries three per-standard `advisories` instead of one generic 'not yet embedded' placeholder. FIBO = planned (open EDMC/OMG license, no legal blocker). FHIR = planned structure-only (CC0 for structure; SNOMED / ICD-10 / LOINC bindings NOT bundled, customer configures own licensed sources). ACORD = license-gated (Ontoligent does NOT redistribute; access via owl:imports + per-workspace attestation once the workspace holds a valid ACORD membership license). Also `skeleton_paused` advisories on the `countries` and `extensions` steps. Locked by `apps/studio/tests/test_composer_advisories.py` (6/6). FE `Composer.tsx` renders `AdvisoryTiles` with color-coded availability badges and a collapsible legal notice per tile.",
    status: "done",
    link: { to: "/studio/composer", label: "Composer" },
  },
  {
    id: "A.4",
    ref: "Track A.4",
    title: "Homepage industry-availability messaging",
    detail:
      "`Welcome.tsx` `DomainPack` now carries an explicit `PackAvailability` union (`available` / `in_progress` / `not_available`) — TS enforces exhaustiveness. Insurance retitled 'Ontoligent Insurance — Party, Policy, Claims, Financial, Regulatory' (badges = IDD / Solvency II / IFRS 17 / DORA, not the FIBO / ACORD standards Ontoligent does not ship). New Banking KYC tile (AMLD5 / 6 / PSD2 / FATCA), `available`. Life Sciences / SecOps / Legal / Healthcare all flipped to `in_progress` with honest availability notes. Tab-strip shows Live / WIP / N/A per-tab micro-badge; pack detail card carries a prominent color-coded availability banner + `availability_note`.",
    status: "done",
    link: { to: "/", label: "Public homepage" },
  },
  {
    id: "readme-reality",
    ref: "Docs",
    title: "analysis3/readme.md — reality-check header on the ✅ scaffold",
    detail:
      "The 'Complete Ontology File Inventory' block in `analysis3/readme.md` was authored as an aspirational plan-of-record and every entry was marked ✅. Reality on disk: 8 Tier 2 insurance packs shipped; T4 LOB is one merged `lob-insurance.draft.ttl` (Home + Health only, refused by draft_gate); T5 Regional is one merged `regional-overlays.draft.ttl` (DE / UK / XL only, refused); T3 health / prevention and `ont-operations.ttl` do not exist; `pipelines/kg_ingestion_pipeline.py`, `mappings/*.rml.ttl`, `.github/workflows/ontology-*.yml` do not exist. Added a `⚠️ REALITY CHECK` header pointing readers at `INVESTIGATION_TRACKING.md` as the authoritative status source, with a corrected 3-state legend (shipped / draft-gated / not authored).",
    status: "done",
  },
  {
    id: "spectacular",
    ref: "Ops",
    title: "drf-spectacular IPAddressField fixup",
    detail:
      "`AuditEventSerializer.ip` overridden as `CharField(read_only=True)` — schema regenerates cleanly for this field. Remaining 256 'unable to guess serializer' warnings are a separate 48-view `@extend_schema` decoration effort tracked outside this plan.",
    status: "done",
  },
];

const GUARDRAIL_TRACK: TrackItem[] = [
  {
    id: "g1",
    ref: "runtime.py",
    title: "Port supervisor Pydantic pattern into the loop runtime",
    detail:
      "`apps/agents/guardrails.py::AgentInputSchema` + `validate_agent_input` gates every `AgentRun.input` payload at the entry to `_run_agent_in_schema`. Malformed input yields an FAILED run with an `error` trace and structured `agent.run.input_rejected` audit event.",
    status: "done",
    link: { to: "/agents", label: "Agents" },
  },
  {
    id: "g2",
    ref: "runtime.py",
    title: "Validate tool-call arguments before dispatch",
    detail:
      "`validate_tool_args` runs before `plugins.create('tool', ...).invoke(args, ...)`. Enforces required keys, additionalProperties, and top-level scalar types from the tool's advertised JSON Schema. Failures yield a structured error trace + `agent.tool.args_invalid` audit — the LLM sees the error and can self-correct.",
    status: "done",
  },
  {
    id: "g3",
    ref: "core/pii.py",
    title: "Wire `PiiRedactor` as an output guardrail",
    detail:
      "`redact_output` (thin wrapper around `core.pii.redact_text`) runs on the assistant's final message before persisting `run.output`. Honours the `PII_REDACTION` setting (`off`/`auto`/`strict`). Previously redactor only ran on ingestion.",
    status: "done",
  },
  {
    id: "g4",
    ref: "runtime.py",
    title: "Prompt-injection marker check",
    detail:
      "`detect_prompt_injection` scans every `tool_result` blob before it enters the LLM context. Matches on 8 override patterns (`ignore previous instructions`, `<system>` tags, `### new instructions ###`, etc). Hits are replaced with a structured refusal + `agent.tool.injection_refused` audit event.",
    status: "done",
  },
  {
    id: "g5",
    ref: "apps/agents",
    title: "Default new Pro/Enterprise agents to supervisor runtime",
    detail:
      "`resolve_runtime_mode` reads `Workspace.organization.plan` — Pro/Enterprise/Team → `supervisor` by default; Free/Starter → the tool-calling loop (now equally gated). Explicit `runtime` in request body always wins. Wired into both `chat` and `ask` endpoints.",
    status: "done",
    link: { to: "/agents", label: "Agents" },
  },
];

function StatusPill({ status }: { status: Status }) {
  const map: Record<Status, { label: string; className: string; Icon: typeof CheckCircle2 }> = {
    done: {
      label: "Done",
      className: "border-good/30 bg-good/12 text-good",
      Icon: CheckCircle2,
    },
    "in-progress": {
      label: "In progress",
      className: "border-accent/30 bg-accent/12 text-accent",
      Icon: Circle,
    },
    "not-started": {
      label: "Not started",
      className: "border-border bg-panel/60 text-muted",
      Icon: Circle,
    },
    blocked: {
      label: "Blocked",
      className: "border-bad/30 bg-bad/12 text-bad",
      Icon: AlertTriangle,
    },
  };
  const { label, className, Icon } = map[status];
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-[7px] border px-2 py-[3px] font-mono text-[10px] font-semibold uppercase tracking-[0.04em] ${className}`}
    >
      <Icon size={11} strokeWidth={2.4} />
      {label}
    </span>
  );
}

function RefChip({ ref: refText }: { ref: string }) {
  return (
    <span className="inline-flex items-center rounded-[7px] border border-accent/30 bg-accent/12 px-2 py-[3px] font-mono text-[10px] font-semibold uppercase tracking-[0.04em] text-accent">
      {refText}
    </span>
  );
}

function TrackRow({ item }: { item: TrackItem }) {
  return (
    <li className="grid gap-3 border-t border-border p-4 first:border-t-0 md:grid-cols-[210px_1fr_120px] md:items-start">
      <div className="flex flex-wrap items-center gap-2">
        <RefChip ref={item.ref} />
        {item.owner && (
          <span className="rounded-[7px] border border-border bg-panel/60 px-2 py-[3px] font-mono text-[10px] uppercase tracking-[0.04em] text-muted">
            {item.owner}
          </span>
        )}
      </div>
      <div className="min-w-0">
        <div className="text-[13.5px] font-semibold text-text">{item.title}</div>
        <p className="mt-1 text-[12.5px] leading-relaxed text-muted">{item.detail}</p>
        {item.link && (
          <Link
            to={item.link.to}
            className="mt-2 inline-flex items-center gap-1 text-[11.5px] font-medium text-accent hover:underline"
          >
            {item.link.label}
            <ExternalLink size={11} />
          </Link>
        )}
      </div>
      <div className="md:justify-self-end">
        <StatusPill status={item.status} />
      </div>
    </li>
  );
}

function TrackSection({
  eyebrow,
  title,
  intro,
  items,
  Icon,
}: {
  eyebrow: string;
  title: string;
  intro: string;
  items: TrackItem[];
  Icon: typeof ClipboardList;
}) {
  const done = items.filter((i) => i.status === "done").length;
  return (
    <section className="rounded-xl border border-border bg-panel/60">
      <header className="flex flex-col gap-3 border-b border-border px-6 py-5 md:flex-row md:items-center md:justify-between">
        <div>
          <div className="mb-1 flex items-center gap-2 font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-accent">
            <Icon size={12} strokeWidth={2.4} />
            {eyebrow}
          </div>
          <h2 className="text-[19px] font-semibold leading-tight text-text">{title}</h2>
          <p className="mt-1 max-w-[720px] text-[12.5px] leading-relaxed text-muted">{intro}</p>
        </div>
        <div className="rounded-[7px] border border-border bg-bg/40 px-3 py-2 text-right font-mono text-[11px] uppercase tracking-[0.06em] text-muted">
          <div className="text-[10px]">Progress</div>
          <div className="mt-[2px] text-[13px] font-semibold text-text">
            {done}
            <span className="text-muted"> / {items.length}</span>
          </div>
        </div>
      </header>
      <ul className="p-0">
        {items.map((item) => (
          <TrackRow key={item.id} item={item} />
        ))}
      </ul>
    </section>
  );
}

export function InstructionPlan() {
  const total = TECHNICAL_TRACK.length + GUARDRAIL_TRACK.length;
  const done =
    TECHNICAL_TRACK.filter((i) => i.status === "done").length +
    GUARDRAIL_TRACK.filter((i) => i.status === "done").length;
  const inProgress =
    TECHNICAL_TRACK.filter((i) => i.status === "in-progress").length +
    GUARDRAIL_TRACK.filter((i) => i.status === "in-progress").length;

  return (
    <div className="mx-auto max-w-[1080px] px-6 py-10">
      <header className="mb-8">
        <div className="mb-2 flex items-center gap-2 font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-accent">
          <BookOpen size={12} strokeWidth={2.4} />
          Instruction plan
        </div>
        <h1 className="text-[26px] font-semibold leading-tight text-text">
          Enterprise Semantic Template &amp; Scaffolding Platform
        </h1>
        <p className="mt-2 max-w-[760px] text-[13.5px] leading-relaxed text-muted">
          Consolidated todo tracker for the Enterprise composer, the pack catalogue,
          and the agent-runtime guardrail parity work. References map back to
          <code className="ml-1 rounded-[5px] border border-border bg-bg/40 px-1.5 py-[1px] font-mono text-[11px] text-text">
            analysis3/INVESTIGATION_TRACKING.md
          </code>
          . Status is derived from repository state and updated as work lands.
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          <span className="rounded-[7px] border border-good/30 bg-good/12 px-2.5 py-1 font-mono text-[10.5px] font-semibold uppercase tracking-[0.04em] text-good">
            Done {done}
          </span>
          <span className="rounded-[7px] border border-accent/30 bg-accent/12 px-2.5 py-1 font-mono text-[10.5px] font-semibold uppercase tracking-[0.04em] text-accent">
            In progress {inProgress}
          </span>
          <span className="rounded-[7px] border border-border bg-panel/60 px-2.5 py-1 font-mono text-[10.5px] font-semibold uppercase tracking-[0.04em] text-muted">
            Total {total}
          </span>
        </div>
      </header>

      <div className="space-y-6">
        <TrackSection
          eyebrow="Track 1"
          title="Remaining work — checklist for the technical team"
          intro="Cross-phase gaps identified during the Enterprise composer evaluation. Each item is scoped to a specific INVESTIGATION_TRACKING phase and can be closed independently."
          items={TECHNICAL_TRACK}
          Icon={ClipboardList}
        />

        <TrackSection
          eyebrow="Track 2"
          title={`What's missing to close the gate before shipping "Enterprise-grade guardrails" as a public claim`}
          intro="Guardrail parity between the default tool-calling loop runtime and the fully Pydantic-gated supervisor runtime. All five items are prerequisites to marketing the platform as Enterprise-grade in public copy."
          items={GUARDRAIL_TRACK}
          Icon={ShieldCheck}
        />
      </div>

      <footer className="mt-8 rounded-xl border border-border bg-panel/60 p-5 text-[12.5px] leading-relaxed text-muted">
        <div className="mb-1 font-semibold text-text">How this list is maintained</div>
        Status labels are hand-edited alongside code changes — this page is a
        source-of-truth tracker for the Enterprise evaluation, not a runtime
        query. When a track item lands, flip its status in
        <code className="mx-1 rounded-[5px] border border-border bg-bg/40 px-1.5 py-[1px] font-mono text-[11px] text-text">
          src/pages/InstructionPlan.tsx
        </code>
        in the same PR that closes the gap.
      </footer>
    </div>
  );
}

export default InstructionPlan;
